import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toastr } from "react-redux-toastr";
import ErrorMessage from "../../../../components/text/ErrorMessage";
import { DropdownList, Multiselect } from "react-widgets";
import { Customer, Address, UpdateCompanyData, Company, City } from "../../../../core/api/models";
import api from "../../../../api";
import Countries from "../../../../utils/countries.json";
import States from "../../../../utils/states.json";
import { decodeHtmlEntities } from "../../../../utils/common";

const AddCompanyAddress: React.FunctionComponent<{
  error?: string;
  onClose: () => void;
  updateCompany: (details: Company) => void;
  selectedAddress?: Address;
  companyDetails?: Company;
}> = (props) => {
  const { control, handleSubmit, errors, watch, setValue } = useForm<Customer>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });

  const [districtList, setDistrictList] = useState([]);

  const statesList = watch("country") === "India" || !props.selectedAddress?.country ? States : [];

  const [isSubmiting, setIsSubmiting] = useState<boolean>(false);

  const [cities, setCities] = useState<{ name: string; value: string }[]>([]);
  useEffect(() => {
    const fetchCityData = async () => {
      try {
        const cityData = await api.AddNewRequirement.loadAllCities();
        const cityOptions = cityData?.map((item: City) => ({
          value: item._id,
          name: decodeHtmlEntities(item?.cityName),
        }));
        cityOptions.sort((a, b) => a.name.localeCompare(b.name));
        setCities(cityOptions);
      } catch (error) {
        // console.log("Failed to fetch city data:", error);
      }
    };

    fetchCityData();
  }, []);

  const onSubmit = async (data?: any) => {
    setIsSubmiting(true);
    let addresses =
      props.companyDetails && props.companyDetails?.addresses
        ? props.companyDetails?.addresses
        : [];
    if (props && props.selectedAddress) {
      addresses = addresses.filter((x) => x._id !== props.selectedAddress?._id);
    }

    addresses.push({
      _id: props.selectedAddress?._id,
      country: data?.country,
      state: data?.state,
      district: data?.district,
      city: data?.city?.name,
      cityObjectId: data?.city?.value || data?.city,
      address: data?.address,
      postalCode: data?.postalCode,
      isPrimary: false,
    });
    const updateData: UpdateCompanyData = {
      name: props.companyDetails?.name,
      cinNumber: props.companyDetails?.cinNumber,
      fan: props.companyDetails?.fan,
      sector: props.companyDetails?.sector?._id,
      industry: props.companyDetails?.industry?._id,
      companySegment: props.companyDetails?.companySegment?._id,
      // salesType: props.companyDetails?.salesType?._id,
      addresses,
    };
    try {
      const response = await api.company.updateCompnay(updateData, props.companyDetails?._id);
      props.updateCompany(response);
      props.onClose();
      toastr.success("Success", "Address saved successfully");
    } catch (error) {
      if (error?.response?.data?.data?.message) {
        toastr.error("Error", error?.response?.data?.data?.message.join());
      } else {
        toastr.error("Error", error?.response?.data?.message);
      }
    }
    setIsSubmiting(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="bg-gray-100 border-top border-bottom py-3">
        <div className="px-4">
          <h6 className="font-weight-semibold">Add New Address</h6>
        </div>
        <div className="d-flex flex-wrap px-4 py-1 contact-form-row active align-items-baseline">
          <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
            Country<span className="text-danger">*</span>
          </div>
          <div className="col pl-2 pr-0 contact-content d-flex flex-wrap align-items-center">
            <Controller
              control={control}
              rules={{ required: true }}
              name="country"
              defaultValue={props.selectedAddress?.country || "India"}
              render={({ onChange, value, name }) => (
                <DropdownList
                  value={value}
                  defaultValue={value}
                  data={Countries}
                  textField={"name"}
                  valueField={"name"}
                  placeholder={"Select country"}
                  onChange={(val) => {
                    onChange(val.name);
                  }}
                  filter={"contains"}
                />
              )}
            />
            {errors.country && (
              <ErrorMessage>
                {errors.country.type === "required" ? "Country is required" : "Invalid Country"}
              </ErrorMessage>
            )}
          </div>
        </div>
        <div className="d-flex flex-wrap px-4 py-1 contact-form-row active align-items-baseline">
          <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
            State <span className="text-danger">*</span>
          </div>
          <div className="col pl-2 pr-0 contact-content d-flex flex-wrap align-items-center">
            <Controller
              control={control}
              rules={{ required: true }}
              name="state"
              defaultValue={props.selectedAddress?.state}
              render={({ onChange, value, name }) => (
                <DropdownList
                  value={value}
                  defaultValue={value}
                  data={statesList}
                  textField={"state"}
                  valueField={"state"}
                  placeholder={"Select state"}
                  onChange={(val) => {
                    onChange(val.state);
                    setDistrictList(val.districts);
                  }}
                />
              )}
            />
            {errors.state && (
              <ErrorMessage>
                {errors.state.type === "required" ? "State is required" : "Invalid State"}
              </ErrorMessage>
            )}
          </div>
        </div>
        <div className="d-flex flex-wrap px-4 py-1 contact-form-row active align-items-baseline">
          <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
            District <span className="text-danger">*</span>
          </div>
          <div className="col pl-2 pr-0 contact-content d-flex flex-wrap align-items-center">
            <Controller
              control={control}
              rules={{ required: true }}
              name="district"
              defaultValue={props.selectedAddress?.district}
              render={({ onChange, value, name }) => (
                <DropdownList
                  value={value}
                  defaultValue={value}
                  data={districtList?.map((d) => {
                    return {
                      name: d,
                    };
                  })}
                  textField={"name"}
                  valueField={"name"}
                  placeholder={"Select district"}
                  onChange={(val) => {
                    onChange(val.name);
                  }}
                />
              )}
            />
            {errors.district && (
              <ErrorMessage>
                {errors.district.type === "required" ? "District is required" : "Invalid District"}
              </ErrorMessage>
            )}
          </div>
        </div>
        <div className="d-flex flex-wrap px-4 py-1 contact-form-row active">
          <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
            Address <span className="text-danger">*</span>
          </div>
          <div className="col pl-2 pr-0 contact-content d-flex flex-wrap align-items-center">
            <Controller
              control={control}
              rules={{ required: true }}
              name="address"
              defaultValue={props.selectedAddress?.address}
              render={({ onChange, value, name }) => (
                <input
                  type="text"
                  className="form-control rounded-sm font-weight-semibold"
                  placeholder=" Address"
                  name={name}
                  value={value}
                  onChange={onChange}
                  id="address"
                />
              )}
            />
            {errors.address && (
              <ErrorMessage>
                {errors.address.type === "required" ? " Address is required" : "Invalid  Address"}
              </ErrorMessage>
            )}
          </div>
        </div>

        <div className="d-flex flex-wrap px-4 py-1 contact-form-row active align-items-baseline">
          <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
            City <span className="text-danger">*</span>
          </div>
          <div className="col pl-2 pr-0 contact-content d-flex flex-wrap align-items-center">
            <Controller
              name="city"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: "City is required",
                },
              }}
              defaultValue={props.selectedAddress?.cityObjectId}
              render={({ onChange, value }) => (
                <DropdownList
                  value={value}
                  data={cities}
                  busy={cities?.length === 0}
                  textField="name"
                  valueField={"value"}
                  filter="contains"
                  onChange={(val) => {
                    onChange(val);
                    // setValue("preferredLocations", []);
                  }}
                  placeholder="Select City"
                />
              )}
            />
            {errors.city && <ErrorMessage>{errors.city.message}</ErrorMessage>}
          </div>
        </div>

        <div className="d-flex flex-wrap px-4 py-1 contact-form-row active align-items-baseline">
          <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">Zip code</div>
          <div className="col pl-2 pr-0 contact-content d-flex flex-wrap align-items-center">
            <Controller
              control={control}
              // rules={{
              //   required: true,
              //   validate: (value) => {
              //     return !!value.trim();
              //   },
              // }}
              rules={{
                required: false,
                pattern: {
                  value: /^[0-9]{6}$/,
                  message: "Invalid Postal Code",
                },
              }}
              name="postalCode"
              defaultValue={props.selectedAddress?.postalCode}
              render={({ onChange, value, name }) => (
                <input
                  type="text"
                  className="form-control rounded-sm font-weight-semibold"
                  placeholder="Postal Code"
                  name={name}
                  value={value}
                  onChange={onChange}
                  id="postalCode"
                />
              )}
            />
            {errors.postalCode && (
              <ErrorMessage>
                {errors.postalCode.type === "required"
                  ? "Postal Code is required"
                  : "Invalid Postal Code"}
              </ErrorMessage>
            )}
          </div>
        </div>

        <div className="col-12 px-4 mt-2 text-right">
          <div className="px-2">
            <button
              className="btn btn-sm btn-outline-dark mr-1 font-normal"
              onClick={props.onClose}
            >
              Cancel
            </button>
            <button disabled={isSubmiting} className="btn btn-sm btn-primary font-normal">
              Save
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default AddCompanyAddress;
